import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const KNOWN = ["company", "email", "phone", "count", "participants"];

export default function AdminRegistrations() {
  const [regs, setRegs] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [cfg, setCfg] = useState({ fields: [] });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [trainingId, setTrainingId] = useState("");
  const [values, setValues] = useState({});

  const load = () => api.get("/registrations").then((r) => setRegs(r.data)).catch(() => {});
  const loadTrainings = () => api.get("/trainings").then((r) => setTrainings(r.data)).catch(() => {});
  useEffect(() => {
    load(); loadTrainings();
    api.get("/event-reg-form").then((r) => setCfg(r.data || { fields: [] })).catch(() => {});
  }, []);

  const conferences = trainings.filter((t) => t.kind === "konferencja");
  const options = conferences.length ? conferences : trainings;
  const fields = cfg?.fields || [];

  const del = async (id) => {
    if (!window.confirm("Usunąć zgłoszenie?")) return;
    try { await api.delete(`/registrations/${id}`); toast.success("Usunięto"); load(); }
    catch { toast.error("Błąd"); }
  };

  const openAdd = () => { setValues({}); setTrainingId(options[0]?.id || ""); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!trainingId) { toast.error("Wybierz konferencję"); return; }
    setBusy(true);
    try {
      const payload = { company: "", email: "", phone: "", count: 1, participants: "", answers: {} };
      fields.forEach((f) => {
        const v = values[f.id] ?? "";
        if (KNOWN.includes(f.id)) payload[f.id] = f.id === "count" ? (Number(v) || 1) : v;
        else payload.answers[f.id] = { label: f.label, value: v };
      });
      await api.post(`/trainings/${trainingId}/register`, payload);
      toast.success("Uczestnik dodany");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const renderField = (f) => {
    const set = (v) => setValues((s) => ({ ...s, [f.id]: v }));
    if (f.type === "textarea") return <Textarea rows={2} value={values[f.id] || ""} onChange={(e) => set(e.target.value)} data-testid={`reg-add-${f.id}`} className="mt-1.5" />;
    if (f.type === "select") return (
      <select value={values[f.id] || ""} onChange={(e) => set(e.target.value)} data-testid={`reg-add-${f.id}`} className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
        <option value="">— wybierz —</option>
        {(f.options || []).map((o, i) => <option key={i} value={o}>{o}</option>)}
      </select>
    );
    return <Input type={f.type === "number" ? "number" : f.type === "email" ? "email" : "text"} value={values[f.id] || ""} onChange={(e) => set(e.target.value)} data-testid={`reg-add-${f.id}`} className={`mt-1.5 ${f.type === "number" ? "w-32" : ""}`} />;
  };

  const renderAnswers = (r) => {
    const a = r.answers || {};
    const keys = Object.keys(a);
    if (!keys.length) return "—";
    return keys.map((k) => `${a[k]?.label || k}: ${a[k]?.value ?? ""}`).join("\n");
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Uczestnicy konferencji</h2>
          <p className="mt-1 text-sm text-muted-foreground">Zgłoszenia z formularza „Weź udział" oraz dodane ręcznie.</p>
        </div>
        <Button onClick={openAdd} className="gap-1.5 bg-ztk-blue" data-testid="registration-add-btn">
          <Plus className="h-4 w-4" /> Dodaj uczestnika
        </Button>
      </div>

      {regs.length === 0 && <p className="mt-8 text-muted-foreground">Brak zgłoszeń.</p>}

      {regs.length > 0 && (
        <div className="mt-8 overflow-x-auto rounded-2xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Konferencja</th>
                <th className="px-4 py-3 font-medium">Firma</th>
                <th className="px-4 py-3 font-medium">E-mail</th>
                <th className="px-4 py-3 font-medium">Telefon</th>
                <th className="px-4 py-3 font-medium">Osób</th>
                <th className="px-4 py-3 font-medium">Uczestnicy</th>
                <th className="px-4 py-3 font-medium">Dodatkowe pola</th>
                <th className="px-4 py-3 text-right font-medium">Akcje</th>
              </tr>
            </thead>
            <tbody>
              {regs.map((r) => (
                <tr key={r.id} className="border-t border-border bg-card align-top" data-testid={`registration-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-ztk-navy dark:text-white">{r.training_title}</td>
                  <td className="px-4 py-3">{r.company || "—"}</td>
                  <td className="px-4 py-3"><a href={`mailto:${r.email}`} className="text-ztk-blue">{r.email}</a></td>
                  <td className="px-4 py-3">{r.phone || "—"}</td>
                  <td className="px-4 py-3">{r.count}</td>
                  <td className="whitespace-pre-line px-4 py-3 text-muted-foreground">{r.participants || "—"}</td>
                  <td className="whitespace-pre-line px-4 py-3 text-muted-foreground">{renderAnswers(r)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => del(r.id)} className="text-destructive" data-testid={`registration-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="registration-add-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Dodaj uczestnika ręcznie</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Konferencja / wydarzenie</Label>
              <select
                value={trainingId}
                onChange={(e) => setTrainingId(e.target.value)}
                data-testid="reg-add-training"
                className="mt-1.5 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="">— wybierz —</option>
                {options.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            </div>
            {fields.map((f) => (
              <div key={f.id}>
                <Label>{f.label}</Label>
                {renderField(f)}
              </div>
            ))}
            <DialogFooter>
              <Button type="submit" disabled={busy} className="bg-ztk-blue" data-testid="reg-add-submit">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Dodaj uczestnika
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
